import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Pencil, Trash2, X, Save, Image as ImageIcon, Folder, Settings2, Table as TableIcon, LayoutGrid, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import GooglePreview from "./GooglePreview";
import IconPicker from "../../components/admin/IconPicker";
import MediaPicker from "../../components/admin/MediaPicker";
import { Icon } from "../../lib/icons";

const emptyService = { title: "", short_description: "", long_description: "", category: "", image_url: "", youtube_url: "", media_type: "image", featured: false, meta_title: "", meta_description: "", slug: "", keyword: "", icon: "Wrench", sort_order: 0,
  booking_enabled: false, booking_duration_min: 30, booking_buffer_min: 0, booking_price_cents: 0, booking_payment_mode: "free", booking_deposit_percent: 30, booking_auto_confirm: true, booking_capacity_per_slot: 1, booking_description: "" };
const emptyCategory = { name: "", slug: "", description: "", icon: "Folder", color: "#3DB9E8", sort_order: 0 };

export default function ServicesAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edit, setEdit] = useState(null);
  const [catEdit, setCatEdit] = useState(null);
  const [manageCategories, setManageCategories] = useState(false);
  const [mediaPicker, setMediaPicker] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("admin_services_view") || "table");
  useEffect(() => { localStorage.setItem("admin_services_view", viewMode); }, [viewMode]);

  const loadAll = async () => {
    const [svc, cats] = await Promise.all([api.get("/services"), api.get("/service-categories")]);
    setItems(svc.data); setCategories(cats.data);
  };
  useEffect(() => { loadAll(); }, []);

  const saveService = async () => {
    if (!edit.title) return toast.error("Titre obligatoire");
    try {
      if (edit.id) await api.put(`/services/${edit.id}`, edit);
      else await api.post("/services", edit);
      toast.success("Service enregistré");
      setEdit(null); loadAll();
    } catch { toast.error("Erreur d'enregistrement"); }
  };

  const delService = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    await api.delete(`/services/${id}`); toast.success("Supprimé"); loadAll();
  };

  const saveCategory = async () => {
    if (!catEdit.name) return toast.error("Nom obligatoire");
    try {
      if (catEdit.id) await api.put(`/service-categories/${catEdit.id}`, catEdit);
      else await api.post("/service-categories", catEdit);
      toast.success("Catégorie enregistrée");
      setCatEdit(null); loadAll();
    } catch { toast.error("Erreur"); }
  };

  const delCategory = async (id) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    await api.delete(`/service-categories/${id}`); loadAll();
  };

  const catBySlug = (slug) => categories.find(c => c.slug === slug);

  return (
    <div className="p-6 md:p-10" data-testid="admin-services-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">CMS</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Services</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Classez vos services par catégorie. Chaque service est publié sur <code>/services/[slug]</code>.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setManageCategories(true)} data-testid="manage-categories-btn">
            <Folder className="w-4 h-4 mr-1.5" />Catégories ({categories.length})
          </Button>
          <Button onClick={() => setEdit({ ...emptyService })} className="bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90" data-testid="new-service-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau
          </Button>
        </div>
      </header>

      {/* Categories strip */}
      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Filtrer :</span>
          {categories.map(c => {
            const count = items.filter(s => s.category === c.slug).length;
            return (
              <span key={c.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold" style={{ borderColor: c.color + "55", background: c.color + "11", color: c.color }}>
                <Icon name={c.icon || "Folder"} className="w-3 h-3" />
                {c.name} <span className="text-[10px] opacity-60 ml-0.5">({count})</span>
              </span>
            );
          })}
        </div>
      )}

      <div className="mb-3 flex items-center justify-end">
        <div className="flex items-center gap-1 p-1 rounded-full border bg-[hsl(var(--muted))]/40" data-testid="services-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode("table")}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full transition ${viewMode === "table" ? "bg-white shadow text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`}
            data-testid="services-view-table"
          >
            <TableIcon className="w-3.5 h-3.5" /> Tableau
          </button>
          <button
            type="button"
            onClick={() => setViewMode("cards")}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 h-8 rounded-full transition ${viewMode === "cards" ? "bg-white shadow text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`}
            data-testid="services-view-cards"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Cartes
          </button>
        </div>
      </div>

      {viewMode === "table" && (
        <Card className="overflow-hidden border" data-testid="services-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/50 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="text-left p-3 font-bold">Prestation</th>
                  <th className="text-left p-3 font-bold hidden md:table-cell">Catégorie</th>
                  <th className="text-left p-3 font-bold hidden lg:table-cell">Description</th>
                  <th className="text-center p-3 font-bold">Mise en avant</th>
                  <th className="text-center p-3 font-bold hidden sm:table-cell">SEO</th>
                  <th className="text-right p-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {items.map(s => {
                  const cat = catBySlug(s.category);
                  return (
                    <tr key={s.id} className="hover:bg-[hsl(var(--muted))]/30" data-testid={`service-row-${s.slug}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center shrink-0" style={{ background: (cat?.color || "#3DB9E8") + "22", color: cat?.color || "#3DB9E8" }}>
                            {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> : <Icon name={s.icon || "Wrench"} className="w-4 h-4" />}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-[color:var(--brand-navy)] line-clamp-1">{s.title}</div>
                            <div className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono truncate">/{s.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        {cat ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: cat.color + "22", color: cat.color }}>
                            <Icon name={cat.icon || "Folder"} className="w-2.5 h-2.5" />{cat.name}
                          </span>
                        ) : <span className="text-[11px] italic text-[hsl(var(--muted-foreground))]">Sans catégorie</span>}
                      </td>
                      <td className="p-3 hidden lg:table-cell max-w-[280px]">
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))] line-clamp-2">{s.short_description || "—"}</div>
                      </td>
                      <td className="p-3 text-center">
                        {s.featured ? (
                          <Badge className="bg-[color:var(--brand-red)] text-[9px]">Accueil</Badge>
                        ) : <span className="text-[11px] text-[hsl(var(--muted-foreground))]">—</span>}
                      </td>
                      <td className="p-3 text-center hidden sm:table-cell tabular-nums">
                        <span className="text-[11px] font-bold text-[color:var(--brand-navy)]">{s.seo?.score || 0}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => setEdit(s)}
                            className="w-8 h-8 rounded-md border hover:bg-[hsl(var(--muted))]/40 inline-flex items-center justify-center"
                            title="Éditer"
                            data-testid={`edit-service-${s.slug}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => delService(s.id)}
                            className="w-8 h-8 rounded-md border hover:bg-red-50 hover:border-red-300 text-red-600 inline-flex items-center justify-center"
                            title="Supprimer"
                            data-testid={`del-service-${s.slug}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!items.length && (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
                      Aucune prestation. Cliquez sur <strong>Nouveau service</strong> pour commencer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {viewMode === "cards" && (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(s => {
          const cat = catBySlug(s.category);
          return (
            <Card key={s.id} className="border overflow-hidden" data-testid={`admin-service-${s.slug}`}>
              {s.image_url ? (
                <img src={s.image_url} alt="" className="h-32 w-full object-cover" />
              ) : (
                <div className="h-32 w-full bg-[hsl(var(--muted))]/30 flex items-center justify-center"><Icon name={s.icon || "Wrench"} className="w-8 h-8 text-[hsl(var(--muted-foreground))]/40" /></div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: (cat?.color || "#3DB9E8") + "22", color: cat?.color || "#3DB9E8" }}>
                      <Icon name={s.icon || "Wrench"} className="w-4 h-4" />
                    </div>
                    <h3 className="font-display font-bold truncate">{s.title}</h3>
                  </div>
                  {s.featured && <Badge className="bg-[color:var(--brand-red)] shrink-0">Home</Badge>}
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                  {cat ? <span className="font-semibold" style={{ color: cat.color }}>{cat.name}</span> : <span className="italic">Sans catégorie</span>}
                  {" · "}score SEO {s.seo?.score || 0}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEdit(s)} data-testid={`edit-${s.slug}`}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="outline" size="sm" onClick={() => delService(s.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      )}

      {/* --- Modal édition service --- */}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()} data-testid="service-edit-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-display font-bold text-xl">{edit.id ? "Éditer" : "Nouveau service"}</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Titre *</Label><Input value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} data-testid="service-title" /></div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={edit.category || "none"} onValueChange={v => setEdit({ ...edit, category: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="service-category-select"><SelectValue placeholder="Sans catégorie" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sans catégorie —</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description courte</Label><Textarea rows={2} value={edit.short_description} onChange={e => setEdit({ ...edit, short_description: e.target.value })} /></div>
              <div><Label>Description longue</Label><Textarea rows={6} value={edit.long_description} onChange={e => setEdit({ ...edit, long_description: e.target.value })} /></div>

              {/* Image via médiathèque */}
              <div>
                <Label>Image principale</Label>
                <div className="flex items-start gap-3 mt-1.5">
                  <div className="w-32 h-24 rounded-lg border bg-[hsl(var(--muted))]/20 overflow-hidden flex items-center justify-center">
                    {edit.image_url ? (
                      <img src={edit.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-[hsl(var(--muted-foreground))]/40" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Button type="button" variant="outline" onClick={() => setMediaPicker(true)} data-testid="service-media-picker-btn">
                      <ImageIcon className="w-4 h-4 mr-1.5" />Choisir dans la médiathèque
                    </Button>
                    <Input value={edit.image_url || ""} onChange={e => setEdit({ ...edit, image_url: e.target.value })} placeholder="…ou coller une URL" className="text-xs" />
                    {edit.image_url && (
                      <button type="button" onClick={() => setEdit({ ...edit, image_url: "" })} className="text-xs text-red-600 hover:underline">Retirer l'image</button>
                    )}
                  </div>
                </div>
              </div>

              <div><Label>YouTube ID (optionnel)</Label><Input value={edit.youtube_url} onChange={e => setEdit({ ...edit, youtube_url: e.target.value })} placeholder="ex: dQw4w9WgXcQ" /></div>

              {/* Icône visuelle */}
              <div>
                <Label>Icône du service</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="w-14 h-14 rounded-xl border-2 bg-[color:var(--brand-sky)]/10 flex items-center justify-center" data-testid="service-icon-preview">
                    <Icon name={edit.icon || "Wrench"} className="w-7 h-7 text-[color:var(--brand-sky)]" strokeWidth={2} />
                  </div>
                  <IconPicker value={edit.icon || "Wrench"} onChange={v => setEdit({ ...edit, icon: v })} />
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">Taille imposée 28×28 (affichage public uniforme sur toutes les cartes services)</div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div><Label>Mot-clé SEO</Label><Input value={edit.keyword} onChange={e => setEdit({ ...edit, keyword: e.target.value })} /></div>
                <div><Label>Ordre (tri)</Label><Input type="number" value={edit.sort_order ?? 0} onChange={e => setEdit({ ...edit, sort_order: +e.target.value })} /></div>
                <div className="flex items-end gap-2"><Switch checked={edit.featured} onCheckedChange={v => setEdit({ ...edit, featured: v })} id="feat" /><Label htmlFor="feat">Homepage</Label></div>
              </div>

              <div><Label>Slug</Label><Input value={edit.slug} onChange={e => setEdit({ ...edit, slug: e.target.value })} placeholder="auto-généré si vide" /></div>

              {/* --- Réservation en ligne --- */}
              <div className="rounded-xl border-2 border-dashed border-[color:var(--brand-sky)]/40 bg-[color:var(--brand-sky)]/5 p-4 space-y-3" data-testid="service-booking-block">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[color:var(--brand-navy)]">Réservation en ligne</span>
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider">Phase 7</Badge>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Permet au client de réserver ce service via un créneau horaire.</div>
                  </div>
                  <Switch checked={!!edit.booking_enabled} onCheckedChange={v => setEdit({ ...edit, booking_enabled: v })} id="booking-enabled" data-testid="service-booking-enabled" />
                </div>

                {edit.booking_enabled && (
                  <div className="space-y-3 pt-2">
                    <div className="grid md:grid-cols-4 gap-3">
                      <div>
                        <Label>Durée (min)</Label>
                        <Input type="number" min={5} step={5} value={edit.booking_duration_min || 30} onChange={e => setEdit({ ...edit, booking_duration_min: +e.target.value })} data-testid="service-booking-duration" />
                      </div>
                      <div>
                        <Label>Tampon après (min)</Label>
                        <Input type="number" min={0} step={5} value={edit.booking_buffer_min || 0} onChange={e => setEdit({ ...edit, booking_buffer_min: +e.target.value })} />
                      </div>
                      <div>
                        <Label>Prix (€)</Label>
                        <Input type="number" min={0} step={0.01} value={((edit.booking_price_cents || 0) / 100).toFixed(2)} onChange={e => setEdit({ ...edit, booking_price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="service-booking-price" />
                      </div>
                      <div>
                        <Label>Simultanés / créneau</Label>
                        <Input type="number" min={1} step={1} value={edit.booking_capacity_per_slot || 1} onChange={e => setEdit({ ...edit, booking_capacity_per_slot: +e.target.value })} />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label>Mode paiement</Label>
                        <Select value={edit.booking_payment_mode || "free"} onValueChange={v => setEdit({ ...edit, booking_payment_mode: v })}>
                          <SelectTrigger data-testid="service-booking-payment-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Gratuit / à régler sur place</SelectItem>
                            <SelectItem value="deposit">Acompte en ligne</SelectItem>
                            <SelectItem value="full">Plein tarif en ligne</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {edit.booking_payment_mode === "deposit" && (
                        <div>
                          <Label>% d'acompte</Label>
                          <Input type="number" min={1} max={100} step={1} value={edit.booking_deposit_percent || 30} onChange={e => setEdit({ ...edit, booking_deposit_percent: +e.target.value })} />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Switch checked={!!edit.booking_auto_confirm} onCheckedChange={v => setEdit({ ...edit, booking_auto_confirm: v })} id="booking-auto" data-testid="service-booking-auto-confirm" />
                      <Label htmlFor="booking-auto" className="text-sm">Confirmation automatique (sinon validation manuelle admin)</Label>
                    </div>
                    <div>
                      <Label>Description réservation (optionnel)</Label>
                      <Textarea rows={2} value={edit.booking_description || ""} onChange={e => setEdit({ ...edit, booking_description: e.target.value })} placeholder="Texte marketing affiché sur la page de réservation…" />
                    </div>
                  </div>
                )}
              </div>
              <div><Label>Meta title</Label><Input value={edit.meta_title} onChange={e => setEdit({ ...edit, meta_title: e.target.value })} /><div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{edit.meta_title.length}/60</div></div>
              <div><Label>Meta description</Label><Textarea rows={2} value={edit.meta_description} onChange={e => setEdit({ ...edit, meta_description: e.target.value })} /><div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{edit.meta_description.length}/160</div></div>
              <GooglePreview title={edit.meta_title || edit.title} description={edit.meta_description} slug={edit.slug} path="services" />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t">
              <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
              <Button onClick={saveService} className="bg-[color:var(--brand-navy)]" data-testid="save-service"><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
            </div>
          </div>
        </div>
      )}

      {/* --- Modal gestion catégories --- */}
      {manageCategories && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setManageCategories(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()} data-testid="categories-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><Folder className="w-5 h-5" />Catégories de services</h3>
              <button onClick={() => setManageCategories(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {categories.length === 0 && (
                <div className="p-6 border-dashed border-2 rounded-xl text-center text-sm text-[hsl(var(--muted-foreground))]">Aucune catégorie. Créez-en une pour organiser vos services.</div>
              )}
              {categories.map(c => (
                <div key={c.id} className="flex items-center gap-3 p-3 border rounded-lg" data-testid={`category-row-${c.slug}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: c.color + "22", color: c.color }}>
                    <Icon name={c.icon || "Folder"} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono truncate">{c.slug}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCatEdit(c)}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="outline" size="sm" onClick={() => delCategory(c.id)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              <Button onClick={() => setCatEdit({ ...emptyCategory })} className="w-full bg-[color:var(--brand-sky)]" data-testid="new-category-btn">
                <Plus className="w-4 h-4 mr-1" />Nouvelle catégorie
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* --- Modal édition catégorie --- */}
      {catEdit && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setCatEdit(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="category-edit-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-display font-bold">{catEdit.id ? "Éditer catégorie" : "Nouvelle catégorie"}</h3>
              <button onClick={() => setCatEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><Label>Nom *</Label><Input value={catEdit.name} onChange={e => setCatEdit({ ...catEdit, name: e.target.value })} data-testid="category-name" /></div>
              <div><Label>Slug</Label><Input value={catEdit.slug} onChange={e => setCatEdit({ ...catEdit, slug: e.target.value })} placeholder="auto-généré" /></div>
              <div><Label>Description courte</Label><Textarea rows={2} value={catEdit.description} onChange={e => setCatEdit({ ...catEdit, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Icône</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="w-10 h-10 rounded-lg border flex items-center justify-center" style={{ background: catEdit.color + "22", color: catEdit.color }}>
                      <Icon name={catEdit.icon || "Folder"} className="w-5 h-5" />
                    </div>
                    <IconPicker value={catEdit.icon || "Folder"} onChange={v => setCatEdit({ ...catEdit, icon: v })} />
                  </div>
                </div>
                <div><Label>Couleur</Label><Input type="color" value={catEdit.color || "#3DB9E8"} onChange={e => setCatEdit({ ...catEdit, color: e.target.value })} className="h-10" /></div>
              </div>
              <div><Label>Ordre</Label><Input type="number" value={catEdit.sort_order ?? 0} onChange={e => setCatEdit({ ...catEdit, sort_order: +e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t">
              <Button variant="outline" onClick={() => setCatEdit(null)}>Annuler</Button>
              <Button onClick={saveCategory} className="bg-[color:var(--brand-navy)]" data-testid="save-category"><Save className="w-4 h-4 mr-1" />Enregistrer</Button>
            </div>
          </div>
        </div>
      )}

      {mediaPicker && (
        <MediaPicker
          folder="services"
          onPick={url => { setEdit({ ...edit, image_url: url }); setMediaPicker(false); }}
          onClose={() => setMediaPicker(false)}
        />
      )}
    </div>
  );
}
